import { useEffect } from 'react';
import { useTranslation } from 'react-i18next';

import { BillingInfoInitializer } from '@ctct/application-layer';
import { BillingStrategy, createResolvedBillingStrategy } from '@ctct/domain-layer';
import { envs } from '@ctct/infrastructure-layer';
import { Heading, PayflowTopBar } from '@ctct/presentation-layer';
import {
  AlertWidget,
  BillingInfoFormWidget,
  PaymentMethodsWidget,
  UpdateBillingWidget,
  useAlertWidget,
  useEmailPlansWidget,
  useOverdueSummaryWidget,
  usePaymentMethodsWidget,
  useRouterNonUiWidget,
  useUpdateBillingWidget,
  isApplePayParamEnabled,
  useBillingInfoFormWidget,
  defaultUseContactsTier,
  defaultUsePrepay,
  defaultUseSmsBundleTier,
} from '@ctct/react-app-layer';
import {
  createPaymentMethodsHooks,
  createAlertHooks,
  useBillingInfoPageStore,
  createUpdateBillingHooks,
  createEmailPlansHooks,
  createBillingInfoFormHooks,
} from '@ctct/state-client-layer';
import { useBillingAccount, usePaymentStrategy } from '@ctct/state-server-layer';

import { RedirectToStripeBillingInfoPage } from './RedirectToStripeBillingInfoPage';

import './billing-info-page.scss';

const initializer = new BillingInfoInitializer();
const alertHooks = createAlertHooks(useBillingInfoPageStore);
const paymentMethodsHooks = createPaymentMethodsHooks(useBillingInfoPageStore);
const updateBillingHooks = createUpdateBillingHooks(useBillingInfoPageStore);
const emailPlansHooks = createEmailPlansHooks(useBillingInfoPageStore);
const billingInfoFormHooks = createBillingInfoFormHooks(useBillingInfoPageStore);

const emailPlansExternalHooks = {
  useContactsTier: defaultUseContactsTier,
  usePrepay: defaultUsePrepay,
  useSmsBundleTier: defaultUseSmsBundleTier,
};

const paymentMethodsExternalHooks = {
  useRatingCallPayload: () => null,
};

const routerNonUiExternalHooks = {
  useIsHidePromoCodesEnabled: () => false, // no promo code application on this page
};

const linkToDashboard = `https://app${envs.getEnv()}constantcontact.com/home`;
const linkToBillingSettings = `https://app${envs.getEnv()}constantcontact.com/pages/myaccount/settings/billing`;
const isStripeEnabled = false;
const isStripePhase2Enabled = false;
const billingStrategy = createResolvedBillingStrategy(BillingStrategy.InHouse);

export const BillingInfoPage = () => {
  const { t } = useTranslation();
  const isApplePayEnabled = isApplePayParamEnabled();
  const { data: billingAccount } = useBillingAccount();
  const paymentStrategy = usePaymentStrategy({
    billingStrategyQuery: billingStrategy,
  });

  useEmailPlansWidget({
    initializer,
    pageStore: useBillingInfoPageStore,
    hooks: emailPlansHooks,
    externalHooks: emailPlansExternalHooks,
    billingStrategy,
    paymentStrategy,
  });
  useRouterNonUiWidget({
    initializer,
    isStripeEnabled,
    isStripePhase2Enabled,
    externalHooks: routerNonUiExternalHooks,
  });
  useOverdueSummaryWidget({ initializer });

  const alertWidgetProps = useAlertWidget({
    initializer,
    pageStore: useBillingInfoPageStore,
    hooks: alertHooks,
  });

  const paymentMethodsWidgetProps = usePaymentMethodsWidget({
    initializer,
    pageStore: useBillingInfoPageStore,
    hooks: paymentMethodsHooks,
    isApplePayEnabled,
    billingStrategy,
    paymentStrategy,
    disableApplePayAsNewPaymentMethod: true,
    externalHooks: paymentMethodsExternalHooks,
    flags: {
      shouldIgnoreRatingCall: true,
    },
  });

  const updateBillingWidgetProps = useUpdateBillingWidget({
    initializer,
    pageStore: useBillingInfoPageStore,
    hooks: updateBillingHooks,
  });

  const billingInfoFormWidgetProps = useBillingInfoFormWidget({
    initializer,
    pageStore: useBillingInfoPageStore,
    hooks: billingInfoFormHooks,
    billingStrategy,
  });

  useEffect(() => {
    initializer.listeners.on();

    return () => {
      initializer.listeners.off();
    };
  }, []);

  const isStripeAccount = billingAccount?.getIsStripeAccount() ?? false;

  if (isStripeAccount) {
    return <RedirectToStripeBillingInfoPage />;
  }

  return (
    <>
      <PayflowTopBar logoLink={linkToDashboard} exitLink={linkToBillingSettings} />
      <div className="billing-info-page-container">
        <Heading subpager>{t('billing-info-page.heading')}</Heading>

        <AlertWidget {...alertWidgetProps} />

        <PaymentMethodsWidget {...paymentMethodsWidgetProps} />
        <BillingInfoFormWidget {...billingInfoFormWidgetProps} />

        <UpdateBillingWidget {...updateBillingWidgetProps} />
      </div>
    </>
  );
};
